import { useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../api.js";
import { useAuth } from "../auth.js";
import { formatDate, formatTime } from "../format.js";
import { Pagination } from "../Pagination.js";
import { useAsync } from "../useAsync.js";
import {
  AUDIENCE_LABELS,
  inboxApi,
  type AnnouncementAudience,
  type SentAnnouncement,
} from "./api.js";

/**
 * The owner's broadcast composer, and the record of what has been sent.
 *
 * Two audiences and no segmentation. "One family" is deliberately absent: that
 * is an ordinary conversation with that family and is started from the student
 * record or by replying, so an owner writing to one household produces the same
 * conversation a tutor would rather than a broadcast of one.
 */

const AUDIENCES: { value: AnnouncementAudience; label: string; help: string }[] = [
  {
    value: "all_families",
    label: "All families",
    help: "Every Hub account linked to a current private student — parents and the children with their own login.",
  },
  {
    value: "all_staff",
    label: "All staff",
    help: "Every current colleague, including you.",
  },
];

function Sent({ item }: { item: SentAnnouncement }) {
  const read = `${item.readCount} read`;
  const recipients = `${item.recipientCount} ${item.recipientCount === 1 ? "recipient" : "recipients"}`;

  return (
    <article className="slot announcement-sent">
      <div style={{ flex: 1 }}>
        <div className="spread">
          <strong>{item.subject}</strong>
          {item.importance === "important" && <span className="tag warn">Important</span>}
        </div>
        <div className="small muted">
          {AUDIENCE_LABELS[item.audience]} · {recipients} · {read}
        </div>
        <div className="small muted">
          {formatDate(item.publishedAt)} at {formatTime(item.publishedAt)}
          {item.email && (
            <>
              {" · "}
              {item.email.sent} of {item.email.requested} emailed
              {item.email.failed > 0 && `, ${item.email.failed} failed`}
            </>
          )}
        </div>
      </div>
    </article>
  );
}

export function Announcements() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const [audience, setAudience] = useState<AnnouncementAudience>("all_families");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [important, setImportant] = useState(false);
  const [alsoEmail, setAlsoEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Held across retries of the same publication, and cleared only once the
   * server has accepted it. Pressing Publish again after a failure is the same
   * announcement; writing a new one is a new key.
   */
  const attemptId = useRef<string | null>(null);
  const [sentPage, setSentPage] = useState(1);
  const [nonce, setNonce] = useState(0);
  const sent = useAsync(() => inboxApi.sentAnnouncements(sentPage), [sentPage, nonce]);

  async function publish(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    attemptId.current ??= crypto.randomUUID();
    try {
      const result = await inboxApi.publishAnnouncement(attemptId.current, {
        audience,
        subject,
        body,
        importance: important ? "important" : "normal",
        alsoEmail,
      });
      attemptId.current = null;
      setSubject("");
      setBody("");
      setImportant(false);
      setAlsoEmail(false);
      setNonce((value) => value + 1);
      navigate(`/inbox/${encodeURIComponent(result.threadId)}`, {
        state: { inboxAccountId: me?.user.id ?? null },
      });
    } catch (cause) {
      /**
       * The words changed between the attempt and the retry.
       *
       * The server refuses that rather than sending the new text to the old
       * announcement's frozen audience. The draft is still on screen, so the
       * only thing to fix is the identity: the next press is a new
       * announcement, which is what the owner has actually written.
       */
      if (cause instanceof ApiError && cause.code === "announcement_changed") {
        attemptId.current = null;
        setError(
          "The first version of this announcement was already published. Press Publish again to send what you have now as a new announcement.",
        );
        return;
      }
      setError(
        cause instanceof Error ? cause.message : "We could not publish that announcement.",
      );
    } finally {
      setBusy(false);
    }
  }

  const chosen = AUDIENCES.find((option) => option.value === audience);

  return (
    <section aria-labelledby="announcements-heading">
      <h1 id="announcements-heading">Announcements</h1>

      <div className="panel">
        <form className="form-stack" onSubmit={(event) => void publish(event)}>
          <fieldset className="announcement-audience">
            <legend className="small muted">Send to</legend>
            <div className="row">
              {AUDIENCES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={option.value === audience ? "small" : "quiet small"}
                  aria-pressed={option.value === audience}
                  onClick={() => setAudience(option.value)}
                  disabled={busy}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {chosen && <p className="small muted">{chosen.help}</p>}
          </fieldset>

          <label className="field-stack">
            Subject
            <input
              value={subject}
              maxLength={200}
              required
              onChange={(event) => setSubject(event.target.value)}
              disabled={busy}
            />
          </label>
          <label className="field-stack">
            Announcement
            <textarea
              value={body}
              maxLength={5000}
              rows={6}
              required
              onChange={(event) => setBody(event.target.value)}
              disabled={busy}
            />
          </label>

          <label className="row small">
            <input
              type="checkbox"
              checked={important}
              onChange={(event) => setImportant(event.target.checked)}
              disabled={busy}
            />
            Mark as important
          </label>
          <label className="row small">
            <input
              type="checkbox"
              checked={alsoEmail}
              onChange={(event) => setAlsoEmail(event.target.checked)}
              disabled={busy}
            />
            Also email recipients who have an address
          </label>

          <p className="small muted">
            Announcements go one way. Recipients can read this in their Inbox but cannot reply
            to it.
          </p>

          {error && <div className="error">{error}</div>}
          <div className="actions-end">
            <button type="submit" disabled={busy || subject.trim() === "" || body.trim() === ""}>
              {busy ? "Publishing…" : "Publish announcement"}
            </button>
          </div>
        </form>
      </div>

      <h2>Sent</h2>
      <div className="panel">
        {sent.loading && !sent.data && <p className="empty">Loading what has been sent…</p>}
        {sent.error && !sent.data && (
          <div className="error">We could not load sent announcements.</div>
        )}
        {sent.data && sent.data.items.length === 0 && (
          <p className="empty">Nothing has been announced yet.</p>
        )}
        {sent.data?.items.map((item) => <Sent key={item.messageId} item={item} />)}
        {sent.data && sent.data.pages > 1 && (
          <Pagination
            page={sent.data.page}
            pages={sent.data.pages}
            ariaLabel="Sent announcements pagination"
            onPageChange={setSentPage}
          />
        )}
      </div>
    </section>
  );
}
